import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';

import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';

import { getCommentLink, getUserAccountId } from '../feedCard-helpers.ts';
import type { User, CommentField } from '../../feedCardTypes.ts';
import { RenderedContent } from '../RenderedContent.tsx';
import { FeedCardContext } from '../FeedCard/FeedCardContext.ts';
import { useFeedShouldHideComments } from '../useFeedShouldHideComments.ts';

import { CommentInfoLine } from './CommentInfoLine.tsx';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
});

const profileWrapperStyles = xcss({
	marginRight: 'space.150',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullWidth = styled.div({
	flexGrow: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-anchor -- To migrate as part of go/ui-styling-standard
const ContentLink = styled.a({
	cursor: 'pointer',
	position: 'absolute',
	width: '100%',
	height: '100%',
	zIndex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentContent = styled.div({
	position: 'relative',
	marginBottom: token('space.050'),
	borderRadius: token('radius.large'),
	background: token('color.background.input'),
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommenterName = styled.div({
	fontWeight: token('font.weight.medium'),
	marginBottom: token('space.050'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentInlineText = styled.div({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	background: token('color.background.warning'),
	width: 'fit-content',
	maxWidth: '100%',
	padding: `0px ${token('space.025')}`,
	marginLeft: token('space.negative.025'),
	marginTop: token('space.negative.025'),
	marginBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommenterNameContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

type CommentProps = {
	user: User;
	comment: CommentField;
	pageId?: string;
	contentType?: string;
	compactComment?: boolean;
};

export const Comment: FC<CommentProps> = ({ user, comment, pageId, contentType }) => {
	const authorName = user.displayName;
	const url = getCommentLink(comment);
	const authorId = getUserAccountId(user);
	const authorAvatar = user?.profilePicture?.path;
	const inlineText = 'inlineText' in comment.location ? comment.location.inlineText : null;
	const commentId = comment.id;
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	const onContentLinkClicked = useCallback(() => {
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'feedCardViewComment',
			commentId,
			includeNavdex: true,
		});
	}, [fireAnalyticsEvent, commentId]);

	const compactComment = useFeedShouldHideComments();

	return (
		<Wrapper>
			{!compactComment && (
				<Box xcss={profileWrapperStyles}>
					<ProfileAvatar userId={authorId} src={authorAvatar} canViewProfile size="small" />
				</Box>
			)}
			<FullWidth>
				<CommentContent>
					<ContentLink
						href={url}
						onClick={onContentLinkClicked}
						data-testid="comment-content-link"
					/>
					{!compactComment && (
						<CommenterNameContainer>
							<CommenterName>{authorName}</CommenterName>
						</CommenterNameContainer>
					)}
					{inlineText && <CommentInlineText>{inlineText}</CommentInlineText>}
					<RenderedContent content={comment} />
				</CommentContent>
				{!compactComment && (
					<CommentInfoLine comment={comment} pageId={pageId} contentType={contentType} />
				)}
			</FullWidth>
		</Wrapper>
	);
};
